import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  useLogoutMutation,
  useGetHomepageSettingsQuery,
} from "../redux/services/api";
import { logoutSuccess } from "../redux/slices/authSlice";
import { selectUser } from "../redux/slices/authSlice";
import { setSettings } from "../redux/slices/homepageSlice";
import {
  selectSettings,
  selectHomepageLoading,
} from "../redux/slices/homepageSlice";
import { useTranslation } from "../hooks/useTranslation";
import { useLanguage } from "../contexts/LanguageContext";
import Icon from "./Icon";

const DashboardLayout = ({ children }) => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { language, toggleLanguage, getLocalizedPath } = useLanguage();

  // Redux state
  const user = useSelector(selectUser);
  const settings = useSelector(selectSettings);
  const settingsLoading = useSelector(selectHomepageLoading);

  // RTK Query hooks
  const [logout, { isLoading: isLoggingOut }] = useLogoutMutation();
  const { data: settingsData } = useGetHomepageSettingsQuery();

  // Load settings into Redux when fetched
  useEffect(() => {
    if (settingsData && settingsData.success) {
      dispatch(setSettings(settingsData.settings));
    }
  }, [settingsData, dispatch]);

  const handleLogout = async () => {
    if (!window.confirm(t("dashboard.logoutConfirm"))) {
      return;
    }

    try {
      await logout().unwrap();
    } catch (err) {
      console.error("Logout error:", err);
    } finally {
      dispatch(logoutSuccess());
      navigate(getLocalizedPath("/login"));
    }
  };

  const menuItems = [
    {
      path: getLocalizedPath("/dashboard"),
      label: t("dashboard.dashboard"),
      icon: "chart-bar",
      exact: true,
    },
    {
      path: getLocalizedPath("/dashboard/homepage"),
      label: t("dashboard.homepageSettings"),
      icon: "home",
    },
    {
      path: getLocalizedPath("/dashboard/special-offers"),
      label: t("dashboard.specialOffers"),
      icon: "tag",
    },
    {
      path: getLocalizedPath("/dashboard/services-categories"),
      label: t("dashboard.servicesCategories"),
      icon: "folder",
    },
    {
      path: getLocalizedPath("/dashboard/services"),
      label: t("dashboard.services"),
      icon: "briefcase",
    },
    {
      path: getLocalizedPath("/dashboard/testimonials"),
      label: t("dashboard.testimonials"),
      icon: "quote-left",
    },
    {
      path: getLocalizedPath("/dashboard/links"),
      label: t("dashboard.linkManagement"),
      icon: "link",
    },
    {
      path: getLocalizedPath("/dashboard/analytics"),
      label: t("dashboard.analytics"),
      icon: "chart-bar",
    },
  ];

  const isActiveRoute = (path, exact = false) => {
    if (exact) {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  return (
    <div
      className={`dashboard-layout-modern ${
        sidebarCollapsed ? "sidebar-collapsed" : ""
      }`}
    >
      {/* Sidebar */}
      <aside className={`sidebar-modern ${!sidebarCollapsed ? "open" : ""}`}>
        <div className="sidebar-header">
          <div className="sidebar-logo">
            {!settingsLoading && settings?.branding?.logo ? (
              <img
                src={settings.branding.logo}
                alt={settings.branding.companyName || "Company Logo"}
                className="sidebar-logo-image"
              />
            ) : null}
          </div>
          <button
            className="sidebar-toggle"
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          >
            <Icon
              name={sidebarCollapsed ? "arrowRight" : "arrowLeft"}
              size="16px"
            />
          </button>
        </div>

        <div className="sidebar-scrollable">
          <nav className="sidebar-nav">
            {menuItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`nav-item-modern ${
                  isActiveRoute(item.path, item.exact) ? "active" : ""
                }`}
              >
                <Icon name={item.icon} className="nav-icon" size="18px" />
                {!sidebarCollapsed && (
                  <span className="nav-label">{item.label}</span>
                )}
              </Link>
            ))}
          </nav>

          <div className="sidebar-footer">
            <div className="user-info">
              <div className="user-avatar-small">
                {user?.name?.charAt(0) || "A"}
              </div>
              {!sidebarCollapsed && (
                <div className="user-details">
                  <span className="user-name">
                    {user?.name || t("dashboard.adminUser")}
                  </span>
                  <span className="user-role">
                    {t("dashboard.administrator")}
                  </span>
                </div>
              )}
            </div>

            {/* Language Switcher Button */}
            <button
              className="language-switcher-btn"
              onClick={toggleLanguage}
              title={sidebarCollapsed ? t("language.switchLanguage") : ""}
            >
              <Icon name="globe" className="language-icon" size="16px" />
              {!sidebarCollapsed && (
                <span>
                  {language === "en"
                    ? t("language.arabic")
                    : t("language.english")}
                </span>
              )}
            </button>

            <button
              className="logout-btn"
              onClick={handleLogout}
              title={sidebarCollapsed ? t("dashboard.logout") : ""}
            >
              <Icon name="sign-out" className="logout-icon" size="16px" />
              {!sidebarCollapsed && <span>{t("dashboard.logout")}</span>}
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="main-content-modern">
        {/* Top Header */}
        <header className="top-header-modern">
          <div className="header-left">
            <button
              className="mobile-menu-btn"
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            >
              <Icon name="bars" size="18px" />
            </button>
            <div className="breadcrumb">
              <span className="breadcrumb-item">
                {t("dashboard.dashboard")}
              </span>
              {location.pathname !== "/dashboard" && (
                <>
                  <span className="breadcrumb-separator">/</span>
                  <span className="breadcrumb-item">
                    {menuItems.find((item) => isActiveRoute(item.path))?.label}
                  </span>
                </>
              )}
            </div>
          </div>

          <div className="header-right">
            <div className="user-menu">
              <div className="user-avatar-header">
                {user?.name?.charAt(0) || "A"}
              </div>
              <div className="user-menu-dropdown">
                <div className="dropdown-header">
                  <div className="dropdown-user-info">
                    <div className="dropdown-avatar">
                      {user?.name?.charAt(0) || "A"}
                    </div>
                    <div className="dropdown-details">
                      <span className="dropdown-name">
                        {user?.name || "Admin User"}
                      </span>
                      <span className="dropdown-email">
                        {user?.email || "admin@silverfort.com"}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="dropdown-menu">
                  <button
                    className="dropdown-item logout"
                    onClick={handleLogout}
                  >
                    <Icon
                      name="sign-out"
                      className="dropdown-icon"
                      size="16px"
                    />
                    Logout
                  </button>
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="page-content-modern">{children}</main>
      </div>
    </div>
  );
};

export default DashboardLayout;
